import { afterNextRender, ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MediaRecording, MediaRecordingDiarizationIntervention } from 'src/app/models/media-recording.model';
import { resolveMediaRecordingDiarizationInterventions } from '../media-recording-diarization-page/media-recording-diarization-page.component';

@Component({
  selector: 'app-meeting-record-diarization-readonly-panel',
  templateUrl: './meeting-record-diarization-readonly-panel.component.html',
  styleUrls: ['./meeting-record-diarization-readonly-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatFormFieldModule, MatSelectModule],
})
export class MeetingRecordDiarizationReadonlyPanelComponent {
  media_recordings = input.required<MediaRecording[]>();

  seek_seconds = input<number | null>(null);

  protected selected_recording_id = signal<number | null>(null);

  protected highlighted_intervention_index = signal<number | null>(null);

  constructor() {
    effect(() => {
      const recordings = this.diarizable_recordings();

      if (recordings.length > 0 && this.selected_recording_id() === null) {
        this.selected_recording_id.set(recordings[0].id);
      }
    });

    effect(() => {
      const seconds = this.seek_seconds();

      if (seconds === null) {
        return;
      }

      afterNextRender(() => {
        this.scrollToInterventionAtSeconds(seconds);
      });
    });
  }

  protected diarizable_recordings = computed(() =>
    this.media_recordings().filter(
      recording => recording.transcription_status === 'completed' && resolveMediaRecordingDiarizationInterventions(recording).length > 0
    )
  );

  protected selected_interventions = computed((): MediaRecordingDiarizationIntervention[] => {
    const recording_id = this.selected_recording_id();
    const recording = this.diarizable_recordings().find(item => item.id === recording_id);

    if (!recording) {
      return [];
    }

    return resolveMediaRecordingDiarizationInterventions(recording);
  });

  protected onRecordingSelected(recording_id: number | null): void {
    this.selected_recording_id.set(recording_id);
    this.highlighted_intervention_index.set(null);
  }

  protected formatInterventionTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remaining_seconds = Math.floor(seconds % 60);

    return `${String(minutes).padStart(2, '0')}:${String(remaining_seconds).padStart(2, '0')}`;
  }

  protected resolveInterventionUserLabel(intervention: MediaRecordingDiarizationIntervention): string {
    if (intervention.user?.full_name) {
      return intervention.user.full_name;
    }

    if (intervention.user_id) {
      return `Usuario #${intervention.user_id}`;
    }

    return 'Sin asignar';
  }

  protected isInterventionHighlighted(index: number): boolean {
    return this.highlighted_intervention_index() === index;
  }

  private scrollToInterventionAtSeconds(seconds: number): void {
    const interventions = this.selected_interventions();

    if (interventions.length === 0) {
      return;
    }

    let target_index = interventions.findIndex(
      intervention => intervention.start_seconds <= seconds && intervention.end_seconds >= seconds
    );

    if (target_index < 0) {
      target_index = interventions.reduce((closest_index, intervention, index) => {
        const closest_intervention = interventions[closest_index];
        const closest_distance = Math.abs(closest_intervention.start_seconds - seconds);
        const current_distance = Math.abs(intervention.start_seconds - seconds);

        return current_distance < closest_distance ? index : closest_index;
      }, 0);
    }

    this.highlighted_intervention_index.set(target_index);

    const element = document.getElementById(`intervention-readonly-${target_index}`);

    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
